import React, { useEffect, useRef, useState } from "react";
import Chart from "chart.js/auto";

const HAFHDirectorCalculator = () => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const [form, setForm] = useState({
    institutions: 1,
    hostels: 1,
    studentsPerHostel: 100,
    basePrice: 100,
    booster: false,
    selfPct: 40,
    coordPct: 30,
    trusteePct: 20,
    ownerPct: 10,
  });

  const [result, setResult] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setForm({
      ...form,
      [name]: type === "checkbox" ? checked : Number(value),
    });
  };

  const calculate = () => {
    const totalStudents =
      form.institutions * form.hostels * form.studentsPerHostel;

    const monthlyRevenue = totalStudents * form.basePrice;
    const annualRevenue = monthlyRevenue * 12;

    let selfShare = (form.selfPct / 100) * annualRevenue;
    const coordShare = (form.coordPct / 100) * annualRevenue;
    const trusteeShare = (form.trusteePct / 100) * annualRevenue;
    const ownerShare = (form.ownerPct / 100) * annualRevenue;

    let boost = 0;

    if (form.booster) {
      boost = 0.03 * annualRevenue;
      selfShare += boost;
    }

    const data = {
      totalStudents,
      monthlyRevenue,
      annualRevenue,
      selfShare,
      coordShare,
      trusteeShare,
      ownerShare,
      boost,
    };

    setResult(data);
    drawChart(data);
  };

  const drawChart = (data) => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }

    const ctx = chartRef.current.getContext("2d");

    chartInstance.current = new Chart(ctx, {
      type: "pie",
      data: {
        labels: ["Self (Director)", "Coordinator", "Trustee", "Owner"],
        datasets: [
          {
            data: [
              data.selfShare,
              data.coordShare,
              data.trusteeShare,
              data.ownerShare,
            ],
            backgroundColor: ["#22c55e", "#3b82f6", "#f59e0b", "#ef4444"],
          },
        ],
      },
      options: {
        plugins: {
          legend: {
            labels: { color: "#e2e8f0" },
          },
        },
      },
    });
  };

  useEffect(() => {
    calculate();
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white [&_p]:text-white p-4">

      {/* Header */}
      <div className="text-center mb-4">
        <h1 className="text-lg lg:text-2xl font-bold">
          📊 HAFH Director Calculator
        </h1>
        <p className="text-xs lg:text-sm text-gray-400">
          Revenue + Commission + Boost Simulation
        </p>
      </div>

      {/* Inputs */}
      <div className="bg-slate-900 p-4 rounded-xl mb-4">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 text-xs lg:text-sm">
            <div className="flex flex-col space-y-2">
                <p>Institutions</p>
                <input
                    className="p-2 rounded bg-slate-800"
                    type="number"
                    name="institutions"
                    value={form.institutions}
                    onChange={handleChange}
                    placeholder="Institutions"
                />
            </div>
            <div className="flex flex-col space-y-2">
                <p>Hostels per Institution</p>
                <input
                    className="p-2 rounded bg-slate-800"
                    type="number"
                    name="hostels"
                    value={form.hostels}
                    onChange={handleChange}
                    placeholder="Hostels per Institution"
                />
            </div>
            <div className="flex flex-col space-y-2">
                <p>Students per Hostel</p>
                <input
                    className="p-2 rounded bg-slate-800"
                    type="number"
                    name="studentsPerHostel"
                    value={form.studentsPerHostel}
                    onChange={handleChange}
                    placeholder="Students per Hostel"
                />
            </div>
            <div className="flex flex-col space-y-2">
                <p>Base Price (₹)</p>
                <input
                    className="p-2 rounded bg-slate-800"
                    type="number"
                    name="basePrice"
                    value={form.basePrice}
                    onChange={handleChange}
                    placeholder="Base Price (₹)"
                />
            </div>

          <label className="flex items-center gap-2 text-xs lg:text-sm">
            <input
              type="checkbox"
              name="booster"
              checked={form.booster}
              onChange={handleChange}
            />
            🚀 Apply Booster (+3% Director Only)
          </label>
        </div>
      </div>

      {/* Allocation */}
      <div className="bg-slate-900 p-4 rounded-xl mb-4">
        <h2 className="text-yellow-200 p-2 text-sm lg:text-lg">Revenue % Distrubution</h2>
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-3 text-xs lg:text-sm">
            <div className="flex flex-col space-y-2">
                <p>Self %</p>
                <input className="p-2 bg-slate-800 rounded"
                    type="number"
                    name="selfPct"
                    value={form.selfPct}
                    onChange={handleChange}
                    placeholder="Self %"
                />
            </div>

            <div className="flex flex-col space-y-2">
                <p>Coordinator %</p>
                <input className="p-2 bg-slate-800 rounded"
                    type="number"
                    name="coordPct"
                    value={form.coordPct}
                    onChange={handleChange}
                    placeholder="Coordinator %"
                />
            </div>

            <div className="flex flex-col space-y-2">
                <p>Trustee / Institutions %</p>
                <input className="p-2 bg-slate-800 rounded"
                    type="number"
                    name="trusteePct"
                    value={form.trusteePct}
                    onChange={handleChange}
                    placeholder="Trustee %"
                />
            </div>
        
            <div className="flex flex-col space-y-2">
                <p>Hostel Owner %</p>
                <input className="p-2 bg-slate-800 rounded"
                    type="number"
                    name="ownerPct"
                    value={form.ownerPct}
                    onChange={handleChange}
                    placeholder="Owner %"
                />
            </div>
        </div>

        <button
          onClick={calculate}
          className="text-xs lg:text-sm w-40 lg:w-48 mt-3 bg-green-500 text-black font-bold py-2 rounded"
        >
          <i className="fa-solid fa-chart-line mr-2"></i>
          Generate Dashboard
        </button>
      </div>

      {/* Result */}
      {result && (
        <div className="bg-slate-900 p-4 rounded-xl text-xs lg:text-sm mb-4">

          <p>Total Students: <b>{result.totalStudents}</b></p>
          <p>Monthly Revenue: ₹{result.monthlyRevenue.toFixed(2)}</p>
          <p>Annual Revenue: ₹{result.annualRevenue.toFixed(2)}</p>

          <hr className="my-2 border-slate-700" />

          <p>Self (Director): ₹{result.selfShare.toFixed(2)}</p>
          <p>Coordinator: ₹{result.coordShare.toFixed(2)}</p>
          <p>Trustee: ₹{result.trusteeShare.toFixed(2)}</p>
          <p>Owner: ₹{result.ownerShare.toFixed(2)}</p>

          <p className="text-green-400">
            Boost Added: ₹{result.boost.toFixed(2)}
          </p>
        </div>
      )}

    {/* Chart */}
    <div className="bg-slate-900 p-4 rounded-xl w-full lg:w-1/2 flex items-center justify-center">
        <div className="w-full max-w-md justify-center">
            <canvas ref={chartRef}></canvas>
        </div>
    </div>

      {/* FontAwesome CDN reminder */}
      <link
        rel="stylesheet"
        href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.0/css/all.min.css"
      />
    </div>
  );
};

export default HAFHDirectorCalculator;